import React, { useEffect, useState } from "react";
import { api } from "../../api/axios";
import { styled } from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import { useDispatch } from "react-redux";
import { fetchCurrentTerm } from "../../redux/slices/term";
import { useAppContext } from "../../contexts/Context";
import { AllSessions } from "../../constants/AllSessions";
import { AllTerms } from "../../configs/AllTerms";
import { CircularProgress } from "../../components/custom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

export default function ResultsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { termName, setTermName, activeSession, setActiveSession } =
    useAppContext();

  const [selectedSession, setSelectedSession] = useState(activeSession || "");
  const [selectedTerm, setSelectedTerm] = useState(termName || "");
  const [report, setReport] = useState(null);
  const [historicalClass, setHistoricalClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [after, setAfter] = useState(false);

  //fetch current term to pre-fill dropdowns
  useEffect(() => {
    dispatch(fetchCurrentTerm())
      .unwrap()
      .then((res) => {
        setTermName(res?.term);
        setActiveSession(res?.session);
        setSelectedTerm(res?.term);
        setSelectedSession(res?.session);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getResults = async () => {
    if (!selectedSession || !selectedTerm) {
      return alert("Please select a Term and Session before checking results.");
    }
    setChecking(true);
    setAfter(false);
    try {
      // New endpoint: looks up by admission number using query params
      // (path params can't be used since session value contains '/')
      const response = await api.get(`/results/student`, {
        params: {
          admissionNumber: user?.admissionNumber,
          session: selectedSession,
          term: selectedTerm,
        },
      });
      setReport(response.data.result);
      setHistoricalClass(response.data.historicalClass);
    } catch (error) {
      console.error("Error fetching results:", error);
      toast.error("Failed to fetch result. Please try again.");
    } finally {
      setChecking(false);
      setAfter(true);
    }
  };

  const onViewResult = () => {
    navigate("/student/filter-results", {
      state: {
        term: selectedTerm,
        session: selectedSession,
        // Pass the historical class so the template renders correctly
        historicalClass,
        // Pass the pre-fetched result row so filterResults doesn't need to re-fetch
        result: report,
      },
    });
  };

  return (
    <>
      {loading && <CircularProgress />}
      <Wrapper className="p-5">
        <div className="">
          <h4>Reports</h4>
          <p>
            View your current and past results here by selecting the term and
            session of the results you wish to check for.
          </p>
          <div className="select-wrapper d-flex flex-row flex-wrap p-3 justify-content-between center container px-4 mt-5">
            <div className="d-flex flex-column gap-1 mt-2">
              <div>
                <h6>Session</h6>
              </div>
              <select
                value={selectedSession}
                onChange={(e) => {
                  setSelectedSession(e.target.value);
                  toast.success(`Session changed to ${e.target.value}`);
                }}
              >
                <option disabled value="">
                  Select Session
                </option>
                {AllSessions?.map((opt, index) => (
                  <option key={index} value={opt.code}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex flex-column gap-1 mt-2">
              <div>
                <h6>Term</h6>
              </div>
              <select
                value={selectedTerm}
                onChange={(e) => {
                  setSelectedTerm(e.target.value);
                  toast.success(`Term changed to ${e.target.value}`);
                }}
              >
                <option disabled value="">
                  Select Term
                </option>
                {AllTerms?.map((opt, index) => (
                  <option key={index} value={opt.code}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="tabs-wrapper py-5 mt-5 d-flex flex-row justify-content-between align-items-center px-3 flex-wrap">
          <button
            onClick={getResults}
            className="check-btn"
            disabled={checking}
          >
            {checking ? "Checking..." : "Check Result"}
          </button>
          {checking && (
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          )}
          {after && (
            <>
              {report ? (
                <p className="m-0">
                  Result for {selectedTerm}, {selectedSession} is{" "}
                  <span className="good-span">available.</span>
                </p>
              ) : (
                <p>
                  Result for {selectedTerm}, {selectedSession} is{" "}
                  <span className="bad-span">not available.</span>
                </p>
              )}
            </>
          )}
        </div>
        <div className="mt-3 py-5 px-3">
          {report && (
            <button className="view-btn w-100" onClick={onViewResult}>
              View Result
            </button>
          )}
        </div>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  background: #f1f1f1 !important;
  height: fit-content !important;
  h6 {
    font-weight: 500 !important;
    font-size: 17px;
    margin: 0 !important;
  }
  p {
    margin: 0 !important;
  }
  .tabs-wrapper {
    background-color: white;
    border-radius: 30px;
  }
  .view-btn {
    border: 1px solid #5ca95c;
    color: white;
    border-radius: 20px;
    padding: 10px 15px;
    background-color: #5ca95c;
    font-weight: 600;
  }
  .check-btn {
    border: 1px solid grey;
    color: white;
    border-radius: 20px;
    padding: 10px 15px;
    background-color: grey;
    font-weight: 300;
  }
  .good-span {
    color: green;
    background-color: #d3d9d3;
    padding: 5px;
    border-radius: 10px;
  }
  .bad-span {
    color: red;
    background-color: white;
    padding: 5px;
    border-radius: 10px;
  }
  .select-wrapper {
    width: 100%;
    background-color: white;
    align-items: center;
    border-radius: 30px;
    span {
      align-items: flex-end;
      justify-content: right;
      color: grey;
    }
    select {
      width: 200px !important;
      padding: 10px;
      border: 1px solid grey;
      color: grey;
      border-radius: 10px;
      background: transparent;
    }
  }
  @media (max-width: 600px) {
    padding: 24px !important;
  }
`;
